import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, Lock, Save, X, Loader2, Check, Globe } from 'lucide-react';
import { languages } from '../i18n/translations';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfileModal = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/profile`, {
        name,
        language: selectedLanguage
      });
      
      updateUser({ name, language: selectedLanguage });
      setLanguage(selectedLanguage);
      toast.success('Perfil actualizado correctamente');
      onClose();
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/profile/password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md glass rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="profile-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-bold text-white">Editar Perfil</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <User className="w-4 h-4 inline-block mr-2" />
              Perfil
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'password'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Lock className="w-4 h-4 inline-block mr-2" />
              Contraseña
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'profile' ? (
              <form onSubmit={handleSaveProfile} className="space-y-5">
                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <Mail className="w-4 h-4 inline-block mr-1" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="form-input bg-white/5 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <User className="w-4 h-4 inline-block mr-1" />
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-input"
                    placeholder="Tu nombre"
                    data-testid="profile-name-input"
                  />
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <Globe className="w-4 h-4 inline-block mr-1" />
                    Idioma
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="form-input"
                    data-testid="profile-language-select"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3 rounded-lg flex items-center justify-center gap-2"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Guardar cambios
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-5">
                {/* Current Password */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Contraseña actual
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="form-input"
                    placeholder="••••••••"
                    required
                    data-testid="current-password-input"
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Nueva contraseña
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input"
                    placeholder="••••••••"
                    minLength={6}
                    required
                    data-testid="new-password-input"
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="••••••••"
                    minLength={6}
                    required
                    data-testid="confirm-password-input"
                  />
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="mt-1 text-sm text-red-400">Las contraseñas no coinciden</p>
                  )}
                  {newPassword && confirmPassword && newPassword === confirmPassword && (
                    <p className="mt-1 text-sm text-green-400 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Las contraseñas coinciden
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || newPassword !== confirmPassword}
                  className="btn-primary w-full py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="change-password-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Cambiar contraseña
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
